import Link from "next/link";
import { cn } from "@/lib/utils";

/** Anthropic-approved partner line (email to Jazz, 2026-10-02). Render it
 *  verbatim and in English on every locale: Anthropic's messaging is not
 *  translated without written consent, so never wrap it in t(). */
export const CLAUDE_PARTNER_APPROVED_LINE =
  "Anchor Sprint is Certified in the Claude Partner Network.";

interface AnthropicPartnerBadgeProps {
  readonly variant?: "hero" | "compact";
  readonly className?: string;
}

/**
 * Text-only credential: Anchor Sprint is Certified in the Claude Partner
 * Network, plus the team's Claude Certified Architect certifications.
 * TRADEMARK SAFETY: no Anthropic or Claude logo, wordmark or wordmark-styled
 * label (the "Anthropic" leading label was removed 2026-10-02 so the chip can
 * never read as "Anthropic Certified"). Plain text in OUR type and colours.
 * Never "Anthropic-certified", "official" or "exclusive", never a tier name
 * other than Certified, never imply endorsement. Keep the footer trademark
 * disclaimer on any page that shows this. The official badge artwork lives in
 * ClaudePartnerCertifiedBadge below and is limited to one per page.
 */
export function AnthropicPartnerBadge({
  variant = "compact",
  className,
}: AnthropicPartnerBadgeProps) {
  if (variant === "hero") {
    return (
      <Link
        href="/partners/anthropic-claude-partner/"
        className={cn(
          "flex min-h-11 min-w-0 items-center gap-2 rounded-xl border border-gray-100/50 bg-white/90 px-3 py-2 shadow-md backdrop-blur-sm transition-all duration-200 hover:scale-[1.01] hover:bg-white hover:shadow-lg sm:inline-flex sm:gap-2.5 sm:px-3.5",
          className,
        )}
      >
        <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
          <span className="text-[11px] font-semibold text-gray-800 sm:text-xs">
            Certified in the Claude Partner Network
          </span>
          <span className="text-[9px] leading-tight text-gray-500 sm:text-[10px]">
            Claude Certified Architects on the team
          </span>
        </span>
      </Link>
    );
  }

  return (
    <span className={cn("inline-flex flex-col items-center gap-1", className)}>
      <span className="inline-flex items-center gap-2 rounded-full border border-primary-contrast/15 bg-primary-contrast/[0.04] px-4 py-1.5 text-sm font-medium text-primary-contrast/85 backdrop-blur-sm">
        Certified in the Claude Partner Network
      </span>
      <span className="text-xs font-medium text-primary-contrast/60">
        Claude Certified Architects on the team
      </span>
    </span>
  );
}

/**
 * The official Claude Partner Network "Certified / Services Partner" badge,
 * rendered unmodified next to the approved line. Partner Co-Marketing
 * Guidelines (June 2026), all binding:
 *  - ONE badge per page, and only on the hero of /partners/anthropic-claude-partner/
 *    and /singapore/claude-partner/, plus once in the announcement post
 *    /blog/claude-partner-network-certified/ (en/ms/zh). Never next to our logo or any other
 *    third-party logo (MDEC, HRD Corp...), never in a row of credentials.
 *  - Minimum 300px on the longest edge: the horizontal badge is drawn at a
 *    fixed 300x95 on every viewport (it fits a 360px screen inside the gutters).
 *  - Clear space at least 2x the height of the word "Certified" (~28px at
 *    300px wide): the white panel gives 32px on desktop and >=30px on mobile,
 *    where the panel runs full-bleed so (360 - 300) / 2 = 30px each side.
 *  - The badge's own background is light ivory, so it sits on plain white,
 *    never on our ivory page tone or a tinted block. No border, shadow,
 *    filter, recolour or crop on the badge or its panel.
 */
export function ClaudePartnerCertifiedBadge({
  className,
}: {
  readonly className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-8",
        className,
      )}
    >
      <div className="-mx-4 flex justify-center bg-white px-[30px] py-8 sm:mx-0 sm:w-fit sm:shrink-0 sm:p-8">
        <img
          src="/images/partners/claude-partner-network-certified-horizontal.svg"
          alt="Claude Partner Network Certified Services Partner badge"
          width={300}
          height={95}
          className="block h-[95px] w-[300px] max-w-none"
        />
      </div>
      {/* data-i18n-skip: the client-side DOM translator would otherwise turn
          "Anchor Sprint" into its zh transliteration on /zh, breaking the
          approved line, which must stay verbatim English on every locale. */}
      <p
        data-i18n-skip
        className="max-w-xs text-base font-semibold leading-snug text-neutral-emphasis"
      >
        {CLAUDE_PARTNER_APPROVED_LINE}
      </p>
    </div>
  );
}
